import {Component, ElementRef, ViewChild} from '@angular/core';
import {
  SelectableCalendarComponent
} from "../../../../x-components/calendar/selectable-calendar/selectable-calendar.component";
import {Subscription} from "rxjs";
import {EventDto} from "../../../../../models/event/event-dto";
import {EventHttpService} from "../../../../../management/x-http-requests/event-http.service";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {AnalyticsRouting} from "../../../common/routes/analytics-routing";
import {GlobalDateFormat} from "../../../../../enums/global-date-format";
import {AppRoutes} from "../../../../../enums/routes";
import {GlobalDateService} from "../../../../../_services/global-date.service";
import {AnalyticsHttpService} from "../../../common/http/analytics-http.service";
import {CommonModule, DatePipe} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {EventKind} from "../../../../../enums/event-kind";

@Component({
  selector: 'analytics-event-selector-view',
  templateUrl: './event-selector-view.component.html',
  imports: [
    CommonModule,
    FormsModule,
    SelectableCalendarComponent,
    DatePipe,
    RouterLink
  ],
  styleUrls: ['./event-selector-view.component.css']
})
export class EventSelectorViewComponent {
  eventCalendar!: SelectableCalendarComponent;
  @ViewChild('searchInput') searchInput!: ElementRef;
  selectedDate?: Date;
  eventList: EventDto[] = [];
  markedDates: Date[] = [];
  filteredEvents: EventDto[] = [];
  currentSearchSubscription?: Subscription = undefined
  searchValue: string = "";
  protected readonly Array = Array;
  protected readonly GlobalDateFormat = GlobalDateFormat;
  protected readonly Routes = AppRoutes;
  protected readonly AnalyticsRouting = AnalyticsRouting;

  constructor(private eventHttpService: EventHttpService,
              private router: Router,
              private route: ActivatedRoute,
              public globalDateService: GlobalDateService,
              public AnalyseHttpRequestService: AnalyticsHttpService) {
  }

  @ViewChild('eventCalendar') set content(content: SelectableCalendarComponent) {
    if (content) {
      this.eventCalendar = content;
      this.fetchInitialData();
    }
  }

  fetchInitialData(): void {
    this.route.queryParams.subscribe(params => {
      let month = params['month'] ?? new Date().getMonth();
      let year = params['year'] ?? new Date().getFullYear();

      let date = new Date(year, month, 10);
      let calendar = this.eventCalendar;
      this.eventCalendar.initWithDate(date);
      this.fetchVisibleDays(date);
    })
  }

  fetchVisibleDays(currentDate: Date, weekday: number = 1): void {
    let dayCountCurrentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();

    let firstDayOfMonthAsWeekday = ((new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay() - weekday) + 7) % 7;

    let dayCountPreviousMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0).getDate();

    let startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, dayCountPreviousMonth - (firstDayOfMonthAsWeekday - 1));


    let endDate = new Date(startDate.getTime() + new Date(1970, 0, 43).getTime());


    // #664 — `kind: Real`. `Event/List` deliberately answers with BOTH kinds (the events list page and the
    // shift maker want that), so without this the analysis picker listed virtual events and let one be opened
    // — while the name search below, which goes through the already Real-only analysis repository, did not.
    // The calendar's day markers came off the same list, so a virtual event also put a dot on the calendar.
    this.eventHttpService.list(0, 0, startDate, endDate, false, "", EventKind.Real)
      .subscribe((eventDtoList: EventDto[]) => {
        const events = this.realOnly(eventDtoList);
        this.mapEventDtoListToCalendarEventModel(events);
        this.eventList = events;
        this.markedDates = events.map(eventDto => eventDto.start);
        this.filteredEvents = this.eventList;
      });
  }

  searchEventsByName() {
    if (undefined !== this.currentSearchSubscription) {
      this.currentSearchSubscription?.unsubscribe();
    }
    if (this.searchValue.length == 0) {
      this.filteredEvents = this.eventList;
      return;
    }

    this.currentSearchSubscription = this.AnalyseHttpRequestService.searchEventsByName(this.searchValue).subscribe((eventDtoList: EventDto[]) => {
      this.filteredEvents = this.realOnly(eventDtoList);
    });

  }

  /**
   * Drops virtual events (#664) from anything that reaches this view.
   *
   * The server filters both feeds already — the month list through `?kind=0`, the name search through the
   * analysis repository — so this is the second lock, not the first. It earns its place because the failure it
   * guards against is silent: a virtual event that slipped through would be *offered for analysis*, and the
   * page it opens would report an evening of zero revenue as though that were the finding rather than a
   * category error. A deployed SPA also outlives the API it was built against.
   *
   * Filtered as "not Virtual" rather than "is Real" deliberately: {@link EventKind.Real} is `0`, and an event
   * from a response that predates the field carries no `kind` at all — `=== Real` would drop every one of
   * those from the list. Every kind that is not the virtual one is a trading event.
   */
  private realOnly(events: EventDto[]): EventDto[] {
    return events.filter(eventDto => eventDto.kind !== EventKind.Virtual);
  }

  daySelected(date: Date): void {
    if (undefined !== date) {
      this.filteredEvents = this.eventList.filter(eventDto => eventDto.start.getDate() == date.getDate() && eventDto.start.getMonth() == date.getMonth() && eventDto.start.getFullYear() == date.getFullYear());
    } else {
      this.filteredEvents = this.eventList;
    }
  }

  mapEventDtoListToCalendarEventModel(eventDtoList: EventDto[]): void {
    eventDtoList.forEach(eventDto => this.mapEventDtoToCalendarEventModel(eventDto));
  }

  mapEventDtoToCalendarEventModel(eventDto: EventDto): void {
  }
}
